<template>
  <div class="inv-form-page">
    <v-container fluid grid-list-xl>
      <v-layout row wrap>
        <!-- ===== Hero ===== -->
        <v-flex xs12>
          <div class="list-hero">
            <p class="list-crumb">
              Facturas / {{ isEditing ? 'Editar' : 'Nueva' }}
            </p>
            <h1 class="list-title">
              {{ isEditing ? 'Editar Factura' : 'Nueva Factura' }}
            </h1>
            <p class="list-subtitle">
              {{
                isEditing
                  ? 'Actualice los datos de la factura y sus productos.'
                  : 'Registre una factura. Puede agregar varias facturas a un mismo consolidado antes de enviar.'
              }}
            </p>
          </div>
        </v-flex>

        <!-- ===== Consolidado acumulado (solo en modo creación) ===== -->
        <v-flex xs12 v-if="!isEditing && consolidado.length">
          <v-card class="inv-card cons-summary">
            <div class="cons-summary__head">
              <div class="cons-summary__title">
                <v-icon class="cons-summary__icon">layers</v-icon>
                <div>
                  <p class="cons-summary__label">
                    Facturas en este consolidado
                  </p>
                  <div class="cons-summary__meta">
                    <span class="cons-badge">
                      {{ consolidado.length }}
                      {{ consolidado.length === 1 ? 'factura' : 'facturas' }}
                    </span>
                    <span class="cons-total-inline">
                      Peso total: <strong>{{ money(totalWeight) }} kg</strong>
                    </span>
                    <span class="cons-total-inline">
                      Volumen total:
                      <strong>{{ volumeFormat(totalVolume) }} m³</strong>
                    </span>
                    <span class="cons-total-inline">
                      Bultos: <strong>{{ totalPackages }}</strong>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="table-wrap">
              <table class="inv-table">
                <thead>
                  <tr>
                    <th>Distribuidor</th>
                    <th>Laboratorio</th>
                    <th># Factura</th>
                    <th class="th-num">Peso Bruto</th>
                    <th class="th-num">Vol. (m³)</th>
                    <th class="th-center">Bulto</th>
                    <th># Control</th>
                    <th>Fecha recepción</th>
                    <th class="th-center">Pend. Doc.</th>
                    <th>Doc. Transporte</th>
                    <th class="th-actions">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(inv, i) in consolidado" :key="inv._key">
                    <td>{{ nameOf(distributors, inv.distId) }}</td>
                    <td>{{ inv._labName }}</td>
                    <td class="inv-cell--strong">{{ inv.invoice }}</td>
                    <td class="th-num">{{ money(inv.grossWeight) }}</td>
                    <td class="th-num">{{ volumeFormat(inv.volume) }}</td>
                    <td class="th-center">{{ inv.packageNumber }}</td>
                    <td>{{ inv.controlNumber || '—' }}</td>
                    <td>{{ dateFormat(inv.receivedDate) }}</td>
                    <td class="th-center">
                      <span
                        class="cons-flag"
                        :class="
                          inv.pendingDocument
                            ? 'cons-flag--yes'
                            : 'cons-flag--no'
                        "
                      >
                        {{ inv.pendingDocument ? 'Sí' : 'No' }}
                      </span>
                    </td>
                    <td>{{ transportLabel(inv.transportDocumentId) }}</td>
                    <td class="th-actions">
                      <div class="action-group">
                        <v-btn
                          class="btn-edit"
                          icon
                          small
                          title="Editar en el formulario"
                          @click="editFromConsolidado(i)"
                        >
                          <v-icon small>edit</v-icon>
                        </v-btn>
                        <v-btn
                          class="btn-delete"
                          icon
                          small
                          title="Quitar del consolidado"
                          @click="removeFromConsolidado(i)"
                        >
                          <v-icon small>delete</v-icon>
                        </v-btn>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </v-card>
        </v-flex>

        <!-- ===== Editor de factura ===== -->
        <v-flex xs12>
          <v-card class="inv-card">
            <div class="inv-section-head">
              <v-icon class="inv-section-head__icon">receipt_long</v-icon>
              <span>Datos de la factura</span>
            </div>
            <div v-if="locked" class="inv-locked-note">
              <v-icon small class="inv-locked-note__icon">lock</v-icon>
              Esta factura está
              {{ form.fdApproved === 'YES' ? 'aprobada' : 'consolidada' }}. Solo
              puede modificar <strong>Peso Bruto</strong> y
              <strong>Observaciones</strong>.
            </div>
            <v-card-text>
              <v-form data-vv-scope="invoice">
                <v-layout row wrap>
                  <!-- 1. Distribuidor -->
                  <v-flex md6 xs12>
                    <label class="inv-label">Distribuidor</label>
                    <v-select
                      :error-messages="errors.collect('invoice.distributor')"
                      :items="distributors"
                      :disabled="locked"
                      class="inv-input"
                      data-vv-name="distributor"
                      placeholder="Seleccione un distribuidor"
                      no-data-text="Datos no disponibles"
                      solo
                      flat
                      v-model="form.distId"
                      v-on:change="onChangeDistributor"
                      v-validate="'required'"
                    ></v-select>
                  </v-flex>
                  <!-- 2. Laboratorio -->
                  <v-flex md6 xs12>
                    <label class="inv-label">Laboratorio</label>
                    <v-select
                      :error-messages="errors.collect('invoice.laboratory')"
                      :items="laboratories"
                      :disabled="locked"
                      class="inv-input"
                      data-vv-name="laboratory"
                      placeholder="Seleccione un laboratorio"
                      no-data-text="Datos no disponibles"
                      solo
                      flat
                      v-model="form.labId"
                      v-on:change="onChangeLaboratory"
                      v-validate="'required'"
                    ></v-select>
                  </v-flex>
                  <!-- 3. # Factura -->
                  <v-flex md6 xs12>
                    <label class="inv-label"># Factura</label>
                    <v-text-field
                      :error-messages="errors.collect('invoice.invoice')"
                      :disabled="locked"
                      class="inv-input"
                      data-vv-name="invoice"
                      placeholder="Número de factura"
                      solo
                      flat
                      v-model="form.invoice"
                      v-validate="'required'"
                    ></v-text-field>
                  </v-flex>
                  <!-- CIF (USD) -->
                  <v-flex md6 xs12>
                    <label class="inv-label">CIF (USD)</label>
                    <v-text-field
                      :disabled="locked"
                      class="inv-input"
                      placeholder="0.00"
                      prefix="USD"
                      type="number"
                      solo
                      flat
                      v-model="form.cif"
                    ></v-text-field>
                  </v-flex>
                  <!-- 4. Peso Bruto -->
                  <v-flex md6 xs12>
                    <label class="inv-label">Peso Bruto (kg)</label>
                    <v-text-field
                      class="inv-input"
                      placeholder="0.00"
                      suffix="kg"
                      type="number"
                      solo
                      flat
                      v-model="form.grossWeight"
                    ></v-text-field>
                  </v-flex>
                  <!-- 5. Volumen (m³) — va junto al peso: ambas son medidas
                       de la carga y se capturan del mismo documento. -->
                  <v-flex md6 xs12>
                    <label class="inv-label">Volumen (m³)</label>
                    <v-text-field
                      class="inv-input"
                      placeholder="0.000"
                      suffix="m³"
                      type="number"
                      step="0.001"
                      min="0"
                      solo
                      flat
                      v-model="form.volume"
                    ></v-text-field>
                  </v-flex>
                  <!-- 6. Bulto -->
                  <v-flex md6 xs12>
                    <label class="inv-label">Bulto</label>
                    <v-text-field
                      :error-messages="errors.collect('invoice.packageNumber')"
                      :disabled="locked"
                      class="inv-input"
                      data-vv-name="packageNumber"
                      placeholder="Cantidad de bultos"
                      type="number"
                      solo
                      flat
                      v-model="form.packageNumber"
                      v-validate="'required'"
                    ></v-text-field>
                  </v-flex>
                  <!-- Fecha de recepción (automática) -->
                  <v-flex md6 xs12>
                    <label class="inv-label">
                      Fecha de recepción
                      <span class="inv-label__hint">(automática)</span>
                    </label>
                    <v-text-field
                      class="inv-input"
                      append-icon="event"
                      readonly
                      disabled
                      solo
                      flat
                      :value="dateFormat(form.receivedDate)"
                    ></v-text-field>
                  </v-flex>
                  <!-- 8. Pendiente Documento -->
                  <v-flex md6 xs12 class="inv-switch-flex">
                    <label class="inv-label">Pendiente Documento</label>
                    <v-switch
                      class="inv-switch"
                      color="#254f87"
                      hide-details
                      :disabled="locked"
                      :label="
                        form.pendingDocument ? 'Sí, documento pendiente' : 'No'
                      "
                      v-model="form.pendingDocument"
                    ></v-switch>
                  </v-flex>
                  <!-- 9. Documento de Transporte — catálogo administrable
                       desde Administración › Documentos de Transporte. -->
                  <v-flex md6 xs12>
                    <label class="inv-label">Documento de Transporte</label>
                    <v-select
                      :items="transportDocs"
                      :disabled="locked"
                      class="inv-input"
                      placeholder="Seleccione el documento"
                      no-data-text="No hay documentos configurados"
                      solo
                      flat
                      v-model="form.transportDocumentId"
                    ></v-select>
                  </v-flex>
                  <!-- Visible para -->
                  <v-flex md6 xs12>
                    <label class="inv-label">Visible para</label>
                    <v-select
                      :items="clientVisible"
                      :disabled="locked"
                      class="inv-input"
                      placeholder="Seleccione"
                      no-data-text="Datos no disponibles"
                      solo
                      flat
                      v-model="form.visible"
                    ></v-select>
                  </v-flex>
                  <!-- Observaciones -->
                  <v-flex xs12>
                    <label class="inv-label">Observaciones</label>
                    <v-textarea
                      class="inv-input"
                      placeholder="Indique aquí el motivo si el trámite no ha iniciado el proceso (queda registrado para el KPI)."
                      rows="2"
                      solo
                      flat
                      v-model="form.observations"
                    ></v-textarea>
                  </v-flex>
                </v-layout>
              </v-form>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- ===== Productos ===== -->
        <v-flex xs12>
          <v-card class="inv-card">
            <div class="inv-section-head">
              <v-icon class="inv-section-head__icon">inventory_2</v-icon>
              <span>Productos de la factura</span>
              <v-spacer></v-spacer>
              <button
                class="inv-btn inv-btn--secondary"
                :disabled="locked"
                @click="addProduct"
              >
                <v-icon small class="inv-btn__icon">add</v-icon>
                Agregar producto
              </button>
            </div>
            <div class="table-wrap">
              <table class="inv-table">
                <thead>
                  <tr>
                    <th>Nombre del Producto</th>
                    <th>Presentación Comercial</th>
                    <th>Reg. Sanitario</th>
                    <th>Laboratorio</th>
                    <th>Órgano Anuente</th>
                    <th class="th-actions">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <app-product-row
                    v-for="item in InvoiceProducts"
                    :index="getRowItemIndex(item.rowKey)"
                    :key="item.rowKey"
                    :reg-catalog="regCatalog"
                    :organos-anuentes="organosAnuentes"
                    :lab-name="currentLabName"
                    :disabled="locked"
                    :row="item"
                    @set-organo="onSetOrgano"
                  ></app-product-row>
                  <tr v-if="!InvoiceProducts.length">
                    <td colspan="6" class="inv-table__empty">
                      Agregue productos y búsquelos por su Reg. Sanitario.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </v-card>
        </v-flex>

        <!-- ===== Acciones ===== -->
        <v-flex xs12>
          <div class="inv-actions-bar">
            <button
              class="inv-btn inv-btn--ghost"
              @click="$router.push('/facturas/listado')"
            >
              Cancelar
            </button>
            <v-spacer></v-spacer>
            <button
              v-if="!isEditing"
              class="inv-btn inv-btn--secondary"
              :disabled="saving"
              @click="addToConsolidado"
            >
              <v-icon small class="inv-btn__icon">playlist_add</v-icon>
              Agregar al consolidado
            </button>
            <button
              class="inv-btn inv-btn--primary"
              :disabled="saving"
              @click="submit"
            >
              <v-icon small class="inv-btn__icon">send</v-icon>
              {{ submitLabel }}
            </button>
          </div>
        </v-flex>
      </v-layout>
    </v-container>

    <v-dialog max-width="500" v-if="dialog.message" v-model="dialog.show">
      <message
        :key="dialog.key"
        :options="dialog"
        v-on:close="dialog.show = false"
      ></message>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment-timezone'
import ProductRow from './ProductRow.vue'
import message from './../dialog/Message'
import Api from '@/api/index'
import { dateFormat } from '@/helpers/TransitHelper'

const emptyForm = () => ({
  labId: '',
  distId: '',
  invoice: '',
  // Se eliminó de la UI (# Pedido) pero el backend aún lo exige en el payload
  // completo; se conserva oculto con un valor por defecto.
  orderNumber: 'N/A',
  cif: '',
  grossWeight: '',
  volume: '',
  packageNumber: '',
  controlNumber: '',
  receivedDate: moment().format('YYYY-MM-DD'),
  transportDocumentId: null,
  pendingDocument: false,
  visible: '',
  observations: '',
  fdApproved: null,
  fdcontrolNumber: null,
  batches: [],
})

// Placeholders que NO cuentan como consolidación real.
const isRealConsolidation = value => {
  if (!value) return false
  const s = String(value)
    .trim()
    .toUpperCase()
  if (!s) return false
  if (['N/A', 'NA', 'APA', 'EM'].includes(s)) return false
  if (/^0+$/.test(s)) return false
  return true
}

export default {
  props: {
    form: {
      type: Object,
      default: emptyForm,
    },
  },
  components: {
    'app-product-row': ProductRow,
    message,
  },
  computed: {
    ...mapGetters({
      InvoiceProducts: 'invoices/emptyItems',
    }),
    isEditing() {
      return 'id' in this.$route.params
    },
    // Al editar, si la factura está aprobada o consolidada el backend solo
    // persiste Observaciones y Peso Bruto; el resto se bloquea en el editor.
    locked() {
      if (!this.isEditing) return false
      if (this.form.fdApproved === 'YES') return true
      const q = this.$route.query.consolidated
      if (q === '1') return true
      if (q === '0') return false
      return isRealConsolidation(this.form.fdcontrolNumber)
    },
    submitLabel() {
      if (this.isEditing) return 'Guardar cambios'
      if (this.saving) return 'Guardando...'
      const count = this.consolidado.length + (this.isEditorFilled() ? 1 : 0)
      if (count === 0) return 'Enviar factura'
      return count > 1 ? `Enviar consolidado (${count})` : 'Enviar factura'
    },
    currentLabName() {
      return this.nameOf(this.laboratories, this.form.labId)
    },
    totalWeight() {
      return this.consolidado.reduce(
        (sum, i) => sum + Number(i.grossWeight || 0),
        0
      )
    },
    totalVolume() {
      return this.consolidado.reduce((sum, i) => sum + Number(i.volume || 0), 0)
    },
    totalPackages() {
      return this.consolidado.reduce(
        (sum, i) => sum + Number(i.packageNumber || 0),
        0
      )
    },
  },
  mounted() {
    // En edición los productos se cargan en loadInvoice; no limpiar aquí.
    if (!this.isEditing) {
      this.$store.dispatch('invoices/resetEmptyItems')
    }
  },
  data() {
    return {
      laboratories: [],
      distributors: [],
      organosAnuentes: [],
      regCatalog: [],
      consolidado: [],
      saving: false,
      clientVisible: [
        { text: 'AMBOS', value: 'Ambos' },
        { text: 'SOLO LABORATORIO', value: 'Laboratorio' },
        { text: 'SOLO DISTRIBUIDOR', value: 'Distribuidor' },
      ],
      // Se llena desde el catálogo administrable; ya no es una lista fija.
      transportDocs: [],
      dialog: {
        show: false,
        key: 0,
      },
    }
  },
  created() {
    this.$store.dispatch('distributors/getAll', {}).then(response => {
      this.distributors = (response.items || []).map(d => ({
        text: d.name,
        value: d.id,
      }))
    })

    Api.OrganoAnuente.getBasicList().then(response => {
      const list = (response && (response.items || response)) || []
      this.organosAnuentes = list.map(o => ({ name: o.name, id: o.id }))
    })

    // Catálogo de documentos de transporte (Administración › Documentos de
    // Transporte). Se guarda el id, no el texto, para que renombrar un
    // documento no rompa las facturas ya registradas.
    Api.TransportDocument.getActive().then(response => {
      const list = (response && (response.items || response)) || []
      this.transportDocs = (Array.isArray(list) ? list : []).map(d => ({
        text: d.name,
        value: d.id,
      }))
    })

    if (this.isEditing) {
      this.loadInvoice()
    } else if (this.$route.query.invoice) {
      // Prellenar el # de factura cuando se llega desde "Crear y relacionar".
      this.form.invoice = this.$route.query.invoice
    }
  },
  methods: {
    dateFormat,
    nameOf(list, value) {
      const found = list.find(i => i.value === value || i.id === value)
      return found ? found.text || found.name : '—'
    },
    transportLabel(value) {
      const found = this.transportDocs.find(t => t.value === value)
      return found ? found.text : '—'
    },
    money(value) {
      return Number(value || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    // Tres decimales: en aéreo y courier el volumen son fracciones de m³ y
    // redondear a dos perdería el dato.
    volumeFormat(value) {
      return Number(value || 0).toLocaleString('en-US', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      })
    },
    getRowKey() {
      const numeric = Math.random()
        .toString(36)
        .substr(2, 12)
      return `key-${numeric}`
    },
    getRowItemIndex(rowKey) {
      return this.InvoiceProducts.findIndex(i => i.rowKey === rowKey)
    },
    addProduct() {
      this.InvoiceProducts.push({ rowKey: this.getRowKey(), product: null })
    },
    // ==== Consolidado ====
    snapshotCurrent() {
      return {
        _key: this.getRowKey(),
        _labName: this.currentLabName,
        labId: this.form.labId,
        distId: this.form.distId,
        invoice: this.form.invoice,
        orderNumber: this.form.orderNumber || 'N/A',
        cif: this.form.cif,
        grossWeight: this.form.grossWeight,
        volume: this.form.volume,
        packageNumber: this.form.packageNumber,
        controlNumber: this.form.controlNumber,
        receivedDate: this.form.receivedDate,
        transportDocumentId: this.form.transportDocumentId,
        pendingDocument: this.form.pendingDocument,
        visible: this.form.visible,
        observations: this.form.observations,
        products: this.InvoiceProducts.map(p => ({ ...p })),
        batches: this.collectBatches(),
      }
    },
    collectBatches() {
      return this.InvoiceProducts.filter(p => p.batchId).map(p => p.batchId)
    },
    validateCurrent() {
      return this.$validator.validateAll('invoice')
    },
    addToConsolidado() {
      return this.validateCurrent().then(valid => {
        if (!valid) return false
        this.consolidado.push(this.snapshotCurrent())
        this.resetEditor()
        this.notify(
          'Factura agregada al consolidado. Puede registrar otra.',
          'primary'
        )
        return true
      })
    },
    resetEditor() {
      // Mantiene distribuidor/laboratorio para agilizar la carga en lote.
      const keepDist = this.form.distId
      const keepLab = this.form.labId
      this.$emit('update:form', emptyForm())
      Object.assign(this.form, emptyForm(), {
        distId: keepDist,
        labId: keepLab,
      })
      this.$store.dispatch('invoices/resetEmptyItems')
      this.$nextTick(() => this.$validator.reset())
    },
    editFromConsolidado(index) {
      const inv = this.consolidado[index]
      Object.assign(this.form, {
        labId: inv.labId,
        distId: inv.distId,
        invoice: inv.invoice,
        orderNumber: inv.orderNumber || 'N/A',
        cif: inv.cif,
        grossWeight: inv.grossWeight,
        volume: inv.volume,
        packageNumber: inv.packageNumber,
        controlNumber: inv.controlNumber,
        receivedDate: inv.receivedDate,
        transportDocumentId: inv.transportDocumentId,
        pendingDocument: inv.pendingDocument,
        visible: inv.visible,
        observations: inv.observations,
      })
      this.onChangeDistributor(inv.distId)
      this.onChangeLaboratory(inv.labId)
      this.$store.dispatch('invoices/resetEmptyItems')
      inv.products.forEach(p => this.InvoiceProducts.push({ ...p }))
      this.consolidado.splice(index, 1)
    },
    removeFromConsolidado(index) {
      this.consolidado.splice(index, 1)
    },
    // ==== Órgano anuente ====
    // El órgano anuente vive en el producto (de ahí lo leen el listado, los
    // cobros y Contabilidad), así que elegirlo aquí lo guarda en el producto
    // y aplica a todas sus facturas.
    onSetOrgano({ productId, organoAnuenteId }) {
      if (!productId || !organoAnuenteId) return
      const entry = this.regCatalog.find(e => e.productId === productId)
      const saved = entry ? entry.organoAnuenteId : null
      if (saved === organoAnuenteId) return

      Api.Product.setOrganoAnuente(productId, organoAnuenteId).then(
        response => {
          if (response && response.status === 200) {
            this.applyProductOrgano(productId, organoAnuenteId)
            const organo = response.data.organoAnuente
            this.notify(
              `Órgano anuente ${organo.name} guardado en el producto.`,
              'primary'
            )
            return
          }
          this.applyProductOrgano(productId, saved)
          this.notify(
            'No se pudo guardar el órgano anuente del producto.',
            'error'
          )
        }
      )
    },
    // Deja igual el catálogo y todas las filas del mismo producto.
    applyProductOrgano(productId, organoAnuenteId) {
      this.regCatalog.forEach(e => {
        if (e.productId === productId) e.organoAnuenteId = organoAnuenteId
      })
      this.InvoiceProducts.forEach(row => {
        if (row.product && row.product.id === productId) {
          this.$set(row, 'organoAnuenteId', organoAnuenteId)
        }
      })
    },
    // ==== Data loading ====
    onChangeDistributor(id) {
      this.laboratories = []
      this.$store.dispatch('distributors/getById', { id }).then(data => {
        this.laboratories = (data.laboratories || []).map(l => ({
          text: l.name,
          value: l.id,
        }))
      })
    },
    onChangeLaboratory(labId) {
      this.regCatalog = []
      if (!labId) return
      const labName = this.nameOf(this.laboratories, labId)
      this.$store
        .dispatch('products/getBasicListByLaboratory', { labId })
        .then(list => {
          const products = list || []
          return Promise.all(
            products.map(entry => this.loadRegsForProduct(entry, labName))
          )
        })
    },
    loadRegsForProduct(entry, labName) {
      const prod = entry.product || entry
      return this.$store
        .dispatch('products/getAll', { productId: prod.id })
        .then(response => {
          const batches = (response && response.items) || []
          batches.forEach(b => {
            this.regCatalog.push({
              sanitaryReg: b.sanitaryReg,
              productName: prod.name,
              productId: prod.id,
              presentation: b.fxFormula,
              laboratory: labName,
              organoAnuenteId:
                (prod.organoAnuente && prod.organoAnuente.id) ||
                prod.organoAnuenteId ||
                null,
              expirationDate: b.expirationDate,
              batchId: b.id,
            })
          })
        })
    },
    loadInvoice() {
      this.$store
        .dispatch('invoices/getById', { id: this.$route.params.id })
        .then(response => {
          if (!response) {
            this.$router.push('/facturas/listado')
            return
          }
          Object.assign(this.form, {
            id: response.id,
            labId: response.lab_invoices.id,
            distId: response.dist_invoices.id,
            invoice: response.invoiceNumber,
            orderNumber: response.orderNumber || 'N/A',
            cif: response.cif,
            grossWeight: response.grossWeight || '',
            volume: response.volume || '',
            packageNumber: response.packageNumber,
            controlNumber: response.controlNumber,
            receivedDate: response.receivedDate,
            transportDocumentId: response.transportDocumentId || null,
            pendingDocument: !!response.pendingDocument,
            visible: response.clientVisible,
            observations: response.observations,
            fdApproved: response.fdApproved,
            fdcontrolNumber: response.fdcontrolNumber,
          })
          this.onChangeDistributor(this.form.distId)
          this.onChangeLaboratory(this.form.labId)

          // Cargar los productos de la factura en la tabla del editor.
          this.$store.dispatch('invoices/resetEmptyItems')
          const labName = response.lab_invoices
            ? response.lab_invoices.name
            : ''
          const batches = response.InvoiceProductBatches || []
          batches.forEach(item => {
            const batch = item.ProductBatch
            if (!batch) return
            const product = batch.product || {}
            this.InvoiceProducts.push({
              rowKey: this.getRowKey(),
              product: { name: product.name, id: product.id },
              presentation: product.commercialPresentation || batch.fxFormula,
              sanitaryReg: batch.sanitaryReg,
              laboratory: labName,
              batchId: batch.id,
              expirationDate: batch.expirationDate,
              organoAnuenteId:
                (product.organoAnuente && product.organoAnuente.id) ||
                product.organoAnuenteId ||
                null,
            })
          })
        })
    },
    // ==== Submit ====
    // Indica si el editor de abajo tiene datos propios (sin contar
    // distribuidor/laboratorio, que se conservan entre facturas).
    isEditorFilled() {
      return !!(
        this.form.invoice ||
        this.form.cif ||
        this.form.grossWeight ||
        this.form.volume ||
        this.form.packageNumber ||
        this.form.transportDocumentId ||
        (this.InvoiceProducts && this.InvoiceProducts.length)
      )
    },
    submit() {
      if (this.saving) return
      if (this.isEditing) {
        this.doSave({
          ...this.form,
          controlNumber: this.form.controlNumber || this.form.invoice,
          batches: this.collectBatches(),
        })
        return
      }
      this.submitConsolidado()
    },
    async submitConsolidado() {
      // Facturas ya acumuladas + la del editor solo si tiene datos.
      const payloads = this.consolidado.map(inv => this.toPayload(inv))
      if (this.isEditorFilled()) {
        const valid = await this.validateCurrent()
        if (!valid) return
        payloads.push(this.toPayload(this.snapshotCurrent()))
      }
      if (!payloads.length) {
        this.notify('Agregue al menos una factura antes de enviar.', 'error')
        return
      }

      this.saving = true
      const errors = []
      let saved = 0
      const savedIds = []
      for (let i = 0; i < payloads.length; i += 1) {
        // Cada factura se crea individualmente con /invoice/add.
        // eslint-disable-next-line no-await-in-loop
        const response = await this.$store.dispatch(
          'invoices/save',
          payloads[i]
        )
        if (response && response.status === 200) {
          saved += 1
          const id = this.extractInvoiceId(response)
          if (id) savedIds.push(id)
        } else {
          errors.push(
            `Factura ${payloads[i].invoice || i + 1}: ${(response &&
              response.data &&
              response.data.error) ||
              'no se pudo guardar'}`
          )
        }
      }

      if (errors.length) {
        this.saving = false
        this.notify(
          `Se guardaron ${saved} de ${payloads.length} facturas. ${errors.join(
            ' | '
          )}`,
          'error'
        )
        return
      }

      // "Crear y relacionar": si se llegó desde el Listado con un destino de
      // consolidación, en vez de amarrar entre sí las nuevas facturas, se
      // relacionan al destino (agregar al padre existente, o consolidar con
      // la factura base).
      const relateParent = this.$route.query.relateParent
      const relateWith = this.$route.query.relateWith
      if (savedIds.length && (relateParent || relateWith)) {
        const res = relateParent
          ? await this.$store.dispatch('invoices/addToConsolidated', {
              parentId: Number(relateParent),
              invoicesID: savedIds,
            })
          : await this.$store.dispatch('invoices/consolidate', {
              invoicesID: [Number(relateWith), ...savedIds],
            })
        this.saving = false
        if (!res || res.status >= 400) {
          this.notify(
            `Las facturas se guardaron, pero no se pudieron relacionar al consolidado: ${(res &&
              res.data &&
              res.data.error) ||
              'error al relacionar'}. Puede consolidarlas desde el Listado.`,
            'error'
          )
        }
        this.$router.push('/facturas/listado')
        return
      }

      // Amarrar el consolidado (2+ facturas): el backend genera el
      // fdcontrolNumber (CONS-...) y lo asigna a todas. Best-effort: si
      // falla, las facturas quedan guardadas como individuales.
      if (savedIds.length >= 2) {
        const res = await this.$store.dispatch('invoices/consolidate', {
          invoicesID: savedIds,
        })
        if (!res || res.status >= 400) {
          this.saving = false
          this.notify(
            `Las facturas se guardaron, pero no se pudieron amarrar en un consolidado: ${(res &&
              res.data &&
              res.data.error) ||
              'error al consolidar'}. Puede consolidarlas desde el Listado.`,
            'error'
          )
          this.$router.push('/facturas/listado')
          return
        }
      }

      this.saving = false
      this.$router.push('/facturas/listado')
    },
    // /invoice/add devuelve la factura creada con `id` en el nivel superior.
    extractInvoiceId(response) {
      return (response && response.data && response.data.id) || null
    },
    toPayload(inv) {
      return {
        labId: inv.labId,
        distId: inv.distId,
        invoice: inv.invoice,
        orderNumber: inv.orderNumber || 'N/A',
        cif: inv.cif,
        grossWeight: inv.grossWeight,
        volume: inv.volume,
        packageNumber: inv.packageNumber,
        // Se eliminó el campo # Control de la UI; si no tiene valor, se envía
        // el mismo # Factura para no romper la validación del backend.
        controlNumber: inv.controlNumber || inv.invoice,
        receivedDate: inv.receivedDate,
        transportDocumentId: inv.transportDocumentId,
        pendingDocument: inv.pendingDocument,
        visible: inv.visible,
        observations: inv.observations,
        batches: inv.batches,
      }
    },
    doSave(payload) {
      this.$store.dispatch('invoices/save', payload).then(response => {
        if (response && response.status === 200) {
          if (this.isEditing) {
            this.notify(
              'Los datos fueron actualizados exitosamente.',
              'primary'
            )
          } else {
            this.$router.push('/facturas/listado')
          }
        } else {
          this.notify(
            (response && response.data && response.data.error) ||
              'No se pudo guardar la factura.',
            'error'
          )
        }
      })
    },
    notify(text, color) {
      this.dialog = {
        message: text,
        color,
        show: true,
        key: this.dialog.key + 1,
      }
    },
  },
}
</script>

<style scoped>
.inv-form-page {
  background: #f8fafc;
  min-height: 100vh;
  padding-bottom: 16px;
}

/* === Hero === */
.list-hero {
  padding: 4px 0 8px;
}
.list-crumb {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}
.list-title {
  margin: 4px 0;
  font-size: 32px;
  line-height: 1.1;
  color: #0f172a;
  font-weight: 700;
}
.list-subtitle {
  margin: 0;
  font-size: 14px;
  color: #475569;
  max-width: 760px;
}

/* === Card === */
.inv-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  background: #ffffff;
  margin-bottom: 16px;
}

.inv-section-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border-bottom: 1px solid #eef2f7;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
}
.inv-section-head__icon {
  color: #254f87 !important;
}

.inv-locked-note {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #fffbeb;
  color: #92400e;
  font-size: 13px;
  border-bottom: 1px solid #fde68a;
}
.inv-locked-note__icon {
  color: #d97706 !important;
}

/* === Labels & inputs === */
.inv-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 6px;
  letter-spacing: 0.01em;
}
.inv-label__hint {
  color: #94a3b8;
  font-weight: 500;
}

.inv-switch-flex {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}
.inv-switch {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

/* === Botones === */
.inv-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 40px;
  padding: 0 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.18s ease;
  text-transform: none;
}
.inv-btn__icon {
  font-size: 17px !important;
}
.inv-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.inv-btn--ghost {
  background: #ffffff;
  color: #475569;
  border-color: #e2e8f0;
}
.inv-btn--ghost:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}
.inv-btn--secondary {
  background: rgba(37, 79, 135, 0.1);
  color: #254f87;
  border-color: rgba(37, 79, 135, 0.3);
}
.inv-btn--secondary:hover {
  background: rgba(37, 79, 135, 0.18);
}
.inv-btn--primary {
  background: #254f87;
  color: #ffffff;
  border-color: #254f87;
  box-shadow: 0 2px 8px rgba(37, 79, 135, 0.25);
}
.inv-btn--primary:hover {
  background: #1e4272;
  transform: translateY(-1px);
}

.inv-actions-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 0 8px;
}

/* === Consolidado === */
.cons-summary__head {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eef2f7;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
}
.cons-summary__title {
  display: flex;
  align-items: center;
  gap: 12px;
}
.cons-summary__icon {
  color: #254f87 !important;
  font-size: 26px !important;
}
.cons-summary__label {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}
.cons-summary__meta {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 4px;
  flex-wrap: wrap;
}
.cons-badge {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: rgba(37, 79, 135, 0.1);
  color: #254f87;
}
.cons-total-inline {
  font-size: 13px;
  color: #64748b;
}
.cons-total-inline strong {
  color: #0f172a;
}
.cons-flag {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}
.cons-flag--yes {
  background: rgba(234, 88, 12, 0.12);
  color: #c2410c;
}
.cons-flag--no {
  background: #f1f5f9;
  color: #64748b;
}

/* === Tabla === */
.table-wrap {
  width: 100%;
  overflow-x: auto;
}
.inv-table {
  width: 100%;
  border-collapse: collapse;
}
.inv-table thead tr {
  background: #f8fafc;
}
.inv-table th,
.inv-table td {
  text-align: left;
  color: #334155;
  padding: 10px 14px;
  border-bottom: 1px solid #eef2f7;
  font-size: 14px;
  vertical-align: middle;
}
.inv-table th {
  font-size: 12px;
  letter-spacing: 0.2px;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
}
.inv-table__empty {
  text-align: center !important;
  color: #94a3b8;
  padding: 22px !important;
}
.th-num {
  text-align: right;
}
.th-center {
  text-align: center;
}
.th-actions {
  width: 110px;
  text-align: right;
}
.inv-cell--strong {
  font-weight: 700;
  color: #0f172a;
}
.action-group {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
}
.btn-edit {
  border: 1px solid rgba(37, 79, 135, 0.3) !important;
  color: #254f87 !important;
  background: rgba(37, 79, 135, 0.1) !important;
  box-shadow: none !important;
}
.btn-delete {
  border: 1px solid rgba(229, 37, 42, 0.25) !important;
  color: #e5252a !important;
  background: rgba(229, 37, 42, 0.07) !important;
  box-shadow: none !important;
}

@media (max-width: 959px) {
  .list-title {
    font-size: 26px;
  }
  .inv-actions-bar {
    flex-wrap: wrap;
  }
  .inv-actions-bar .inv-btn {
    flex: 1;
  }
}
</style>

<style>
/* === Inputs (clases globales de Vuetify, no scoped) === */
.inv-form-page
  .inv-input.v-text-field.v-text-field--solo
  .v-input__control
  > .v-input__slot,
.inv-form-page
  .inv-input.v-select.v-text-field--solo
  .v-input__control
  > .v-input__slot,
.inv-form-page
  .inv-input.v-textarea.v-text-field--solo
  .v-input__control
  > .v-input__slot {
  background: #ffffff !important;
  border: 1px solid #d1d5db !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  min-height: 42px !important;
}
.inv-form-page
  .inv-input.v-input--is-focused
  .v-input__control
  > .v-input__slot {
  border-color: #254f87 !important;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.12) !important;
}
.inv-form-page
  .inv-input.v-input--is-disabled
  .v-input__control
  > .v-input__slot {
  background: #f8fafc !important;
}
.inv-form-page .inv-input .v-input__icon .v-icon {
  color: #94a3b8 !important;
}
</style>
